import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Trash2, EyeOff, Eye, RefreshCw, Star, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return d;
  }
}

export default function Admin() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("estimates");
  const [estimates, setEstimates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/admin/login", { replace: true });
      return;
    }
    if (!user.is_admin) {
      navigate("/admin/denied", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const load = async () => {
    setLoading(true);
    try {
      const [e, r] = await Promise.all([
        axios.get(`${API}/admin/estimates`),
        axios.get(`${API}/admin/reviews`),
      ]);
      setEstimates(e.data || []);
      setReviews(r.data || []);
    } catch (err) {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  const toggleReview = async (id, approved) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, { approved: !approved });
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, approved: !approved } : r)));
    } catch {
      toast.error("Erreur.");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`);
      setReviews((rs) => rs.filter((r) => r.id !== id));
    } catch {
      toast.error("Erreur.");
    }
  };

  if (authLoading || (!user && loading)) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">Admin</div>
            <div className="font-heading text-lg font-medium text-slate-900">Reflectum Pellicule</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
              <div className="text-sm text-slate-700">{user?.email}</div>
            </div>
            <Button onClick={load} variant="outline" className="rounded-full h-9" data-testid="admin-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => { logout(); navigate("/admin/login", { replace: true }); }} variant="outline" className="rounded-full h-9" data-testid="admin-logout">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center gap-3">
          <TabBtn active={tab === "estimates"} onClick={() => setTab("estimates")} count={estimates.length} testId="tab-estimates">
            Demandes d'estimation
          </TabBtn>
          <TabBtn active={tab === "reviews"} onClick={() => setTab("reviews")} count={reviews.length} testId="tab-reviews">
            Avis clients
          </TabBtn>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="p-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
          ) : tab === "estimates" ? (
            <EstimatesTable rows={estimates} />
          ) : (
            <ReviewsTable rows={reviews} onToggle={toggleReview} onDelete={deleteReview} />
          )}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, count, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
        active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
      }`}
    >
      {children}
      <span className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
    </button>
  );
}

function EstimatesTable({ rows }) {
  if (!rows.length) return <Empty text="Aucune demande d'estimation pour l'instant." />;
  return (
    <div className="space-y-4" data-testid="estimates-list">
      {rows.map((r, i) => (
        <div key={r.id} data-testid={`estimate-row-${i}`} className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-heading text-lg font-semibold text-slate-900">{r.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{fmtDate(r.created_at)}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 capitalize">{r.property_type}</span>
              {r.window_count && (
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{r.window_count} fenêtres</span>
              )}
              {r.preferred_contact_time && (
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{r.preferred_contact_time}</span>
              )}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-slate-700">
            <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-blue-600"><Mail className="w-4 h-4" />{r.email}</a>
            <a href={`tel:${r.phone}`} className="flex items-center gap-2 hover:text-blue-600"><Phone className="w-4 h-4" />{r.phone}</a>
            {r.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{r.address}</div>}
          </div>
          {r.message && (
            <div className="mt-4 p-4 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-700 whitespace-pre-wrap">
              {r.message}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReviewsTable({ rows, onToggle, onDelete }) {
  if (!rows.length) return <Empty text="Aucun avis pour l'instant." />;
  return (
    <div className="space-y-4" data-testid="reviews-list">
      {rows.map((r, i) => (
        <div key={r.id} data-testid={`review-row-${i}`} className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-heading text-lg font-semibold text-slate-900">{r.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {r.city ? `${r.city} · ` : ""}
                {fmtDate(r.created_at)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className={`w-4 h-4 ${s < r.rating ? "fill-yellow-400 text-yellow-400" : "text-slate-300"}`} />
                ))}
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${r.approved ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                {r.approved ? "Publié" : "Masqué"}
              </span>
            </div>
          </div>
          <p className="mt-4 text-slate-700 leading-relaxed">{r.text}</p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => onToggle(r.id, r.approved)} variant="outline" className="rounded-full h-9" data-testid={`toggle-review-${i}`}>
              {r.approved ? <><EyeOff className="w-4 h-4 mr-2" />Masquer</> : <><Eye className="w-4 h-4 mr-2" />Publier</>}
            </Button>
            <Button onClick={() => onDelete(r.id)} variant="outline" className="rounded-full h-9 text-red-600 hover:bg-red-50 border-red-200" data-testid={`delete-review-${i}`}>
              <Trash2 className="w-4 h-4 mr-2" />Supprimer
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="p-12 bg-white rounded-2xl border border-dashed border-slate-300 text-center text-slate-500">
      {text}
    </div>
  );
}
